// The console header. Always visible, spans the main column.
//
// Composition, left-to-right:
//   1. Module label + song-title field
//   2. Live readouts: cycle phasor, CYCLE counter, BPM
//   3. Transport: play, pause/resume, stop, save
//
// Lives above the code IDE. Replaces the old EditorToolbar, which
// was title + transport only. The new deck uses tempo + phase data
// from the Strudel hooks (lifted up to App) so the user gets a
// live readout regardless of whether the PerformanceStrip is up.

import { memo, useCallback, useEffect, useRef, useState } from 'react';
import type { ChangeEvent, MutableRefObject } from 'react';
import type { PlayerPhase } from '../strudel';
import { subscribeTick, useStartedState } from '../strudel';
import { CyclePhasor } from './visualizer/CyclePhasor';
import { TempoScrubber } from './TempoScrubber';
import type { RecorderState } from '../recording/recorder';

type Props = {
  title: string;
  onTitleChange: (title: string) => void;
  onPlay: () => void;
  onPauseToggle: () => void;
  onRestart: () => void;
  onStop: () => void;
  onSave: () => void;
  phase: PlayerPhase;
  /** Live cycle position — drives the inline phasor. */
  nowRef: MutableRefObject<number>;
  /** Mutated by the hap-data loop; read from a ticker-driven rAF here
   *  to update the readout's textContent. No React state in the path. */
  cycleRef: MutableRefObject<number>;
  cpsRef: MutableRefObject<number>;
  /** React state that flips on engine start/stop. Used for the active-
   *  glow palette swap. Doesn't change on every hap frame. */
  startedRef: MutableRefObject<boolean>;
  /** Commits a BPM change to the song via a setcpm edit. */
  onTempoCommit: (bpm: number) => void;
  /** True when the editor buffer differs from the saved song. Drives
   *  the record-LED + the trailing ● next to the title. */
  dirty: boolean;
  /** Opens the left rack overlay on mobile. Ignored at md+. */
  onOpenRack: () => void;
  /** Returns to the Home page from the editor. The wordmark in the
   *  title module doubles as the Home affordance — clicking the
   *  brand mark feels natural and avoids adding extra chrome. */
  onGoHome: () => void;
  /** Current state of the audio recorder — drives the Record button's
   *  label + active visual. */
  recorderState: RecorderState;
  /** Elapsed ms for the active recording. Ignored when idle. */
  recordDurationMs: number;
  /** Toggle recording: start from idle, stop from recording/paused. */
  onToggleRecord: () => void;
  /** Number of takes captured this session — drives the "▸ Takes (n)"
   *  button's visibility. */
  takesCount: number;
  /** Opens the takes modal. Ignored when takesCount === 0. */
  onOpenTakes: () => void;
  /** True when the audio engine has booted at least once so a tap
   *  MediaStream is available. Disables the Record button otherwise. */
  recordingAvailable: boolean;
  /** Shareable `/s/<id>` URL for the active song, or null when it has
   *  never been published (no cloud id yet). Drives the Share button. */
  shareUrl: string | null;
  /** Surface a status-bar note after a link is copied. */
  onCopyStatus: (msg: string, level?: 'ok' | 'warn' | 'err' | '') => void;
};

// Memoized: cycle, bpm, and active come from the hap frame and
// change on discrete events (once per cycle, on tempo changes, on
// play/stop) — not every 12fps poll. The memo skips reconciliation
// on every frame where these primitives are unchanged, which is the
// vast majority during steady playback.
export const TransportDeck = memo(TransportDeckInner);

function TransportDeckInner({
  title,
  onTitleChange,
  onPlay,
  onPauseToggle,
  onRestart,
  onStop,
  onSave,
  phase,
  nowRef,
  cycleRef,
  cpsRef,
  startedRef,
  onTempoCommit,
  dirty,
  onOpenRack,
  onGoHome,
  recorderState,
  recordDurationMs,
  onToggleRecord,
  takesCount,
  onOpenTakes,
  recordingAvailable,
  shareUrl,
  onCopyStatus,
}: Props) {
  const active = useStartedState(startedRef);
  const playDisabled =
    phase !== 'ready' && phase !== 'playing' && phase !== 'paused';
  const pauseDisabled = phase !== 'playing' && phase !== 'paused';
  const paused = phase === 'paused';
  const playing = phase === 'playing';

  return (
    <header
      aria-label="Transport"
      // Safe-area insets push the operator panel below the iPhone
      // Dynamic Island and clear of any landscape notch. The dark
      // gradient bg fills the rack-ear area above the operator
      // panel — the chassis reads as one continuous unit. env()
      // evaluates to 0px on non-notched devices, so desktop is
      // unaffected.
      className="relative flex items-stretch border-b border-zinc-900 bg-gradient-to-b from-zinc-900/80 to-zinc-950 safe-pt safe-pl safe-pr"
    >
      {/* Screw-dot corner accents — Eurorack faceplate detail. The
          top dots track the safe-area inset so they anchor to the
          visible operator panel, not the rack-ear. */}
      <span
        aria-hidden="true"
        className="absolute safe-screw-top safe-screw-left w-1 h-1 rounded-full bg-zinc-800 shadow-[inset_0_0.5px_0.5px_rgba(255,255,255,0.15)]"
      />
      <span
        aria-hidden="true"
        className="absolute safe-screw-top safe-screw-right w-1 h-1 rounded-full bg-zinc-800 shadow-[inset_0_0.5px_0.5px_rgba(255,255,255,0.15)]"
      />
      <span
        aria-hidden="true"
        className="absolute bottom-1.5 left-1.5 w-1 h-1 rounded-full bg-zinc-800 shadow-[inset_0_0.5px_0.5px_rgba(255,255,255,0.15)]"
      />
      <span
        aria-hidden="true"
        className="absolute bottom-1.5 right-1.5 w-1 h-1 rounded-full bg-zinc-800 shadow-[inset_0_0.5px_0.5px_rgba(255,255,255,0.15)]"
      />

      {/* Rack toggle — shown only on narrow viewports. */}
      <button
        type="button"
        onClick={onOpenRack}
        aria-label="Open rack"
        className="md:hidden px-4 flex items-center justify-center text-zinc-400 hover:text-yellow-300 border-r border-zinc-900"
      >
        <svg viewBox="0 0 16 16" width="16" height="16" fill="none" aria-hidden="true">
          <path
            d="M2 4h12M2 8h12M2 12h12"
            stroke="currentColor"
            strokeWidth="1.25"
            strokeLinecap="round"
          />
        </svg>
      </button>

      {/* Title module — label above, input below. */}
      <div className="flex-1 min-w-0 flex items-center gap-3 px-4 py-2.5">
        <div className="flex flex-col min-w-0 flex-1">
          <span className="text-[9px] tracking-[0.32em] uppercase font-display leading-none mb-1.5 whitespace-nowrap overflow-hidden">
            <button
              type="button"
              onClick={onGoHome}
              title="Go home"
              aria-label="Go to home page"
              className="text-zinc-500 hover:text-yellow-300 focus-visible:text-yellow-300 focus-visible:outline focus-visible:outline-1 focus-visible:outline-yellow-300/60 transition-colors cursor-pointer"
            >
              <span aria-hidden="true">●</span>
              {/* Hide the wordmark text on mobile — the dot alone is the
                  Home affordance, and the transport buttons need every
                  pixel of horizontal width on a phone. */}
              <span className="hidden sm:inline"> Strudel Harness</span>
            </button>
            {dirty && (
              <span
                aria-label="Unsaved changes"
                className="ml-2 tracking-normal text-yellow-300 normal-case"
                title="Unsaved changes — ⌘S to save"
              >
                ●
              </span>
            )}
          </span>
          <input
            type="text"
            placeholder="untitled song"
            aria-label="Song title"
            value={title}
            onChange={(e: ChangeEvent<HTMLInputElement>) => onTitleChange(e.target.value)}
            className="w-full bg-transparent border-b border-zinc-800 focus:border-yellow-300 text-zinc-100 text-[14px] font-body pb-1 outline-none transition-colors placeholder:text-zinc-600"
          />
        </div>
      </div>

      {/* Live readouts — phasor + cycle + bpm. Hidden under 640px to
          protect title width; the PerformanceStrip carries them otherwise. */}
      <div className="hidden sm:flex items-center gap-5 px-4 py-2 border-l border-zinc-900 bg-zinc-950/40">
        <CyclePhasor nowRef={nowRef} active={active} size={44} />
        <LiveReadout label="cycle" active={active} refFn={() => String(cycleRef.current).padStart(3, '0')} />
        <TempoScrubber cpsRef={cpsRef} active={active} onCommit={onTempoCommit} />
      </div>

      {/* Transport — physical-feeling buttons. Play glows phosphor
          green while transmitting. */}
      <div
        role="toolbar"
        aria-label="Transport controls"
        // md:hidden + md:flex below — desktop keeps the inline
        // toolbar; mobile uses MobilePedalboard at the bottom of the
        // viewport for thumb-zone access.
        className="hidden md:flex items-center gap-1 sm:gap-1.5 px-2 sm:px-3 py-2 border-l border-zinc-900"
      >
        <TransportButton
          variant="primary"
          label={playing ? '◉ Playing' : '▶ Play'}
          title="Evaluate the current code and play from cycle 0"
          onClick={onPlay}
          disabled={playDisabled}
          active={playing}
        />
        <TransportButton
          variant="secondary"
          label={paused ? '▸ Resume' : '❚❚ Pause'}
          title={paused ? 'Resume from pause' : 'Pause without clearing the pattern'}
          onClick={onPauseToggle}
          disabled={pauseDisabled}
        />
        <TransportButton
          variant="secondary"
          label="↺ Restart"
          title="Restart from cycle 0 with the current code"
          onClick={onRestart}
          disabled={playDisabled}
        />
        <TransportButton
          variant="stop"
          label="■ Stop"
          title="Stop playback"
          onClick={onStop}
        />
        <RecordButton
          state={recorderState}
          durationMs={recordDurationMs}
          onToggle={onToggleRecord}
          available={recordingAvailable}
        />
        {takesCount > 0 && (
          <TakesButton count={takesCount} onOpen={onOpenTakes} />
        )}
        <ShareButton shareUrl={shareUrl} onCopyStatus={onCopyStatus} />
        <SaveButton onSave={onSave} dirty={dirty} />
      </div>
    </header>
  );
}

// Readout whose textContent is mutated by the shared ticker rather
// than passed through as a React prop. The span renders once; its
// value updates happen via direct DOM writes when the refFn result
// changes. Matches the same pattern used in PerformanceStrip.
function LiveReadout({
  label,
  active,
  refFn,
}: {
  label: string;
  active: boolean;
  refFn: () => string;
}) {
  const spanRef = useRef<HTMLSpanElement>(null);
  const lastRef = useRef<string>('');

  useEffect(() => {
    return subscribeTick(() => {
      const next = refFn();
      if (next !== lastRef.current) {
        lastRef.current = next;
        const span = spanRef.current;
        if (span) span.textContent = next;
      }
    });
  }, [refFn]);

  return (
    <div className="flex flex-col items-end">
      <span className="text-[8px] tracking-[0.3em] uppercase text-zinc-500 mb-0.5 leading-none">
        {label}
      </span>
      <span
        ref={spanRef}
        className={
          'font-display text-[22px] leading-none tabular-nums transition-colors ' +
          (active ? 'text-yellow-300' : 'text-zinc-600')
        }
        style={
          active
            ? { textShadow: '0 0 12px rgba(253, 224, 71, 0.35)' }
            : undefined
        }
      >
        ---
      </span>
    </div>
  );
}

// ─── Save button + dirty LED ────────────────────────────────────────

function SaveButton({
  onSave,
  dirty,
}: {
  onSave: () => void;
  dirty: boolean;
}) {
  return (
    <div className="relative flex items-center gap-1.5">
      {/* Record LED — dim when saved, pulsing yellow when dirty. The
          pulse keyframes live in style.css and run only when the data
          attribute is present, so reduced-motion viewers still see
          the steady indicator via the bg color fallback. */}
      <span
        aria-hidden="true"
        data-dirty={dirty || undefined}
        className={
          'save-led h-1.5 w-1.5 rounded-full shrink-0 ' +
          (dirty ? 'bg-yellow-300' : 'bg-zinc-700')
        }
        style={
          dirty
            ? { boxShadow: '0 0 8px rgba(253, 224, 71, 0.6)' }
            : undefined
        }
      />
      <button
        type="button"
        onClick={onSave}
        title={dirty ? 'Save unsaved changes (⌘S)' : 'Nothing to save (⌘S)'}
        aria-label={dirty ? 'Save unsaved changes' : 'Saved'}
        className={
          'px-2 sm:px-3 py-2 text-[10px] font-semibold tracking-[0.18em] uppercase rounded-sm border ' +
          'cursor-pointer transition-all ' +
          'shadow-[inset_0_-1.5px_0_rgba(0,0,0,0.3),inset_0_1px_0_rgba(255,255,255,0.04)] ' +
          'active:translate-y-[1px] active:shadow-[inset_0_1px_0_rgba(0,0,0,0.3)] ' +
          (dirty
            ? 'bg-yellow-300 border-yellow-300 text-zinc-950 hover:brightness-95'
            : 'bg-zinc-800 border-zinc-700 text-zinc-500 hover:text-zinc-200')
        }
      >
        <CompactLabel label={dirty ? '◎ Save' : '✓ Saved'} />
      </button>
    </div>
  );
}

// ─── Share button ───────────────────────────────────────────────────

// Copies the song's `/s/<id>` link to the clipboard with a brief
// "copied" flip. Disabled until the song has been published (no cloud
// id → no shareable link yet). Private songs won't resolve for other
// viewers, so the status note keeps that expectation honest.
function ShareButton({
  shareUrl,
  onCopyStatus,
}: {
  shareUrl: string | null;
  onCopyStatus: (msg: string, level?: 'ok' | 'warn' | 'err' | '') => void;
}) {
  const [copied, setCopied] = useState(false);
  const timer = useRef<number | null>(null);

  useEffect(
    () => () => {
      if (timer.current) window.clearTimeout(timer.current);
    },
    [],
  );

  const handleClick = useCallback(() => {
    if (!shareUrl) return;
    navigator.clipboard
      ?.writeText(shareUrl)
      .then(() => {
        setCopied(true);
        onCopyStatus(
          'Link copied — public & unlisted songs open for anyone; private ones stay private.',
          'ok',
        );
        if (timer.current) window.clearTimeout(timer.current);
        timer.current = window.setTimeout(() => setCopied(false), 1500);
      })
      .catch(() => onCopyStatus('Could not copy link.', 'err'));
  }, [shareUrl, onCopyStatus]);

  const disabled = !shareUrl;
  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={disabled}
      title={
        disabled
          ? 'Publish this song to get a shareable link'
          : 'Copy a shareable link to this song'
      }
      aria-label="Copy share link"
      className={
        'px-2 sm:px-3 py-2 text-[10px] font-semibold tracking-[0.18em] uppercase rounded-sm border ' +
        'cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed transition-all ' +
        'shadow-[inset_0_-1.5px_0_rgba(0,0,0,0.3),inset_0_1px_0_rgba(255,255,255,0.04)] ' +
        'active:translate-y-[1px] active:shadow-[inset_0_1px_0_rgba(0,0,0,0.3)] ' +
        (copied
          ? 'bg-zinc-800 border-cyan-300/60 text-cyan-300'
          : 'bg-zinc-800 border-zinc-700 text-zinc-200 hover:bg-zinc-700')
      }
    >
      <CompactLabel label={copied ? '✓ Copied' : '🔗 Share'} />
    </button>
  );
}

type TransportVariant = 'primary' | 'secondary' | 'stop';

function TransportButton({
  label,
  title,
  onClick,
  disabled,
  variant,
  active,
}: {
  label: string;
  title: string;
  onClick: () => void;
  disabled?: boolean;
  variant: TransportVariant;
  active?: boolean;
}) {
  const base =
    'px-2 sm:px-3 py-2 text-[10px] font-semibold tracking-[0.18em] uppercase rounded-sm border ' +
    'cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed transition-all ' +
    'shadow-[inset_0_-1.5px_0_rgba(0,0,0,0.3),inset_0_1px_0_rgba(255,255,255,0.04)] ' +
    'active:translate-y-[1px] active:shadow-[inset_0_1px_0_rgba(0,0,0,0.3)]';
  let palette = '';
  if (variant === 'primary') {
    palette = active
      ? 'bg-emerald-300 border-emerald-300 text-zinc-950 hover:brightness-95'
      : 'bg-yellow-300 border-yellow-300 text-zinc-950 hover:brightness-95';
  } else if (variant === 'stop') {
    palette = 'bg-rose-400 border-rose-400 text-white hover:brightness-95';
  } else {
    palette =
      'bg-zinc-800 border-zinc-700 text-zinc-100 hover:bg-zinc-700';
  }
  return (
    <button
      type="button"
      title={title}
      aria-label={label}
      onClick={onClick}
      disabled={disabled}
      className={base + ' ' + palette}
    >
      <CompactLabel label={label} />
    </button>
  );
}

// Splits a "[icon] [text]" label into icon-always + text-on-sm+. Lets
// the transport bar fit in a phone viewport without sacrificing the
// brutalist text labels on desktop. The button's aria-label still
// carries the full string for screen readers.
function CompactLabel({ label }: { label: string }) {
  const spaceIdx = label.indexOf(' ');
  if (spaceIdx <= 0) return <>{label}</>;
  const icon = label.slice(0, spaceIdx);
  const text = label.slice(spaceIdx + 1);
  return (
    <>
      <span aria-hidden="true">{icon}</span>
      <span className="hidden sm:inline sm:ml-1">{text}</span>
    </>
  );
}

// ─── Record button + Takes button ───────────────────────────────────

function RecordButton({
  state,
  durationMs,
  onToggle,
  available,
}: {
  state: RecorderState;
  durationMs: number;
  onToggle: () => void;
  available: boolean;
}) {
  const recording = state === 'recording' || state === 'paused';
  const disabled = !available && !recording;
  const label = recording
    ? `● Rec · ${formatDuration(durationMs)}`
    : '● Rec';
  const title = recording
    ? 'Stop recording'
    : available
      ? 'Start recording the audio output'
      : 'Boot the engine with Play before recording';

  const base =
    'px-2 sm:px-3 py-2 text-[10px] font-semibold tracking-[0.18em] uppercase rounded-sm border ' +
    'cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed transition-all ' +
    'shadow-[inset_0_-1.5px_0_rgba(0,0,0,0.3),inset_0_1px_0_rgba(255,255,255,0.04)] ' +
    'active:translate-y-[1px] active:shadow-[inset_0_1px_0_rgba(0,0,0,0.3)]';
  // Active recording = red fill with a gently pulsing dot. Idle = red
  // outline on the zinc background so the button reads as armed.
  const palette = recording
    ? 'bg-red-500 border-red-500 text-white hover:brightness-95 rec-pulse'
    : 'bg-zinc-800 border-red-500/70 text-red-300 hover:bg-zinc-700 hover:text-red-200';

  return (
    <button
      type="button"
      title={title}
      onClick={onToggle}
      disabled={disabled}
      aria-label={recording ? 'Stop recording' : 'Start recording'}
      aria-pressed={recording}
      className={base + ' ' + palette}
    >
      <CompactLabel label={label} />
    </button>
  );
}

function TakesButton({ count, onOpen }: { count: number; onOpen: () => void }) {
  return (
    <button
      type="button"
      onClick={onOpen}
      title={`Open takes (${count})`}
      aria-label={`Open takes (${count})`}
      className={
        'px-2 sm:px-3 py-2 text-[10px] font-semibold tracking-[0.18em] uppercase rounded-sm border ' +
        'cursor-pointer transition-all ' +
        'bg-zinc-800 border-zinc-700 text-zinc-200 hover:bg-zinc-700 ' +
        'shadow-[inset_0_-1.5px_0_rgba(0,0,0,0.3),inset_0_1px_0_rgba(255,255,255,0.04)]'
      }
    >
      <span aria-hidden="true">▸</span>
      <span className="hidden sm:inline sm:ml-1">Takes ({count})</span>
    </button>
  );
}

function formatDuration(ms: number): string {
  const totalSeconds = Math.floor(ms / 1000);
  const m = Math.floor(totalSeconds / 60);
  const s = totalSeconds % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}
